//#region imports
import React, { useState, useEffect, useCallback } from 'react';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { getBookingByFlightNum, selectShowAddPassenger, setShowAddPassenger, } from '_redux/slices/timeline/timeline-data';
import { resetAdditionalPassengers, selectAdditionalPassengers, } from '_redux/slices/prm-bookings/bookings';
import { selectUserDb, selectIata } from '_redux/slices/main-screen/user';
import { resetAdditionalPassengers as resetAdditionalPassengersFormFlags } from "_redux/slices/timeline/timeline-data";
import { addFlightPassenger, resetAddFlightPassenger, selectAddFlightPassengerStatus } from '_redux/slices/timeline/timeline-bookings';
import { selectDefaultCheckInLocation, setFlightModal } from '_redux/slices/timeline/timeline-control';
import { selectPickUpLocations } from '_redux/slices/timeline/timeline-areas';
import { resetMenuContext, setFlightContextPoints, setSelectedFlightForContext, showFlightContext } from '_redux/slices/utils/menu-context';
import { setSelectedFlightToView } from "_redux/slices/timeline/selected-task";

// custom components
import AdditionalPassengerForm from './additional-passenger-form';
import ActionsModal from '_components/molecules/Modals/actions-modal';
import FlightInfoCard from './flight-info-card';
import { formatCarrierCodeAndFlightNumber } from '_utils/functions';

//#endregion

const FlightInformation = (props) => {

    //#region redux
    const dispatch = useDispatch()
    const dbUser = useSelector(selectUserDb)
    const iata = useSelector(selectIata);                                       // holds iata of account

    const additionalPassengers = useSelector(selectAdditionalPassengers);       // additional Passengers to be added to flight 
    const defaultCheckInLocation = useSelector(selectDefaultCheckInLocation);
    const pickUpLocations = useSelector(selectPickUpLocations);
    const showAddPassenger = useSelector(selectShowAddPassenger)
    const addFlightPassengerStatus = useSelector(selectAddFlightPassengerStatus);

    // OPTIMIZED: Combined related state into single object
    const [flightState, setFlightState] = useState({
        arrivalAirport: '',
        departureAirport: '',
        type: '',
        status: '',
        statusType: '',
        statusTime: '',
        statusGate: '',
        stand: '-',
        flightNumber: '',
        displayFlightNumber: '',
        operatingStatus: 'On Time',
        originalTime: '',
        completedFlight: false
    });

    const [flightInfo, setFlightInfo] = useState(null);
    const [landedFlight, setLandedFlight] = useState(false);
    const [flightStatus, setFlightStatus] = useState('');
    //#endregion

    //#region use effect

    useEffect(() => {
        dispatch(resetAddFlightPassenger());
    }, []);

    // OPTIMIZED: Set flight info with single state update
    useEffect(() => {
        // If flight data is passed directly, use it (performance optimization)
        if (props?.flightData) {
            const flightInfo = props.flightData;
            const flightNumber = flightInfo.flightNumber;

            setFlightInfo(flightInfo);
            setFlightState({
                flightNumber,
                displayFlightNumber: flightNumber?.includes('U2') ? flightNumber?.replace('U2', 'EZY') : flightNumber,
                status: flightInfo.status,
                statusType: flightInfo.statusType,
                statusTime: flightInfo.operationTime,
                statusGate: flightInfo.gate,
                stand: flightInfo.stand,
                operatingStatus: flightInfo.operatingStatus || "On Time",
                originalTime: flightInfo?.scheduled || flightInfo?.originalFlightTime,
                arrivalAirport: flightInfo?.destination || "",
                departureAirport: flightInfo?.origin || "",
                completedFlight: flightInfo.completed ?? false,
                type: flightInfo?.type || (flightInfo?.origin === iata ? "departure" : flightInfo?.destination === iata ? "arrival" : "")
            });
        }
    }, [props?.flightData, iata]);
    //#endregion

    //#region functions 
    // OPTIMIZED: Memoized open modal handler
    const openModal = useCallback(() => {
        if (props?.flight?.includes("Estimated") || flightState.flightNumber?.includes("/boarding") || props?.flight?.includes("ALTPickUp")) return;
        dispatch(getBookingByFlightNum(props?.flight))
        dispatch(setShowAddPassenger(props?.flight))
    }, [props?.flight, flightState.flightNumber, dispatch]);

    function formatPaxName(pax) {
        if (!pax.firstName || !pax.secondName) return "PLACEHOLDER";
        return pax.firstName.trim() + ' ' + pax.secondName.trim()
    }

    // OPTIMIZED: Memoized get pax position info
    const getPaxPositionInfo = useCallback((positionInfo) => {
        if (flightState.type === "arrival") return null

        if (Object?.keys(positionInfo)?.length > 0) return positionInfo;

        if (defaultCheckInLocation) {
            return {
                locationId: defaultCheckInLocation,
                locationTitle: pickUpLocations?.find((pickUp) => defaultCheckInLocation == pickUp?._id)?.title,
                activity: "Checked-in",
                time: new Date()
            }
        } else return null
    }, [flightState.type, defaultCheckInLocation, pickUpLocations]);

    // OPTIMIZED: Memoized create booking document
    const createBookingDocument = useCallback((passenger, additionalBoard, departureDate = null) => {
        const tempFlightNumber = flightInfo?.flightNumber ? flightInfo?.flightNumber : flightState.flightNumber;

        const { carrierCode, number } = formatCarrierCodeAndFlightNumber(tempFlightNumber)

        const flightDate = flightInfo?.scheduledDate || flightInfo?.flightDate || flightInfo?.date;

        const passengerName = formatPaxName(passenger)
        const notComplete = !passenger.assistance || !passenger.firstName || !passenger.secondName
        const paxObj = {
            name: passengerName,
            notComplete: notComplete,
            flightDetails: {
                carrierCode: carrierCode?.toUpperCase(),
                flightNumber: number,
                ...(flightInfo?.type === "arrival"
                    ? { arrivalDate: flightDate, ...(departureDate && { departureDate }) }
                    : { departureDate: flightDate }),
            },
            assistance: Array.isFullArray(passenger.assistance) ? passenger.assistance : ["PLACEHOLDER"],
            source: {
                source: "PORTAL",
                data: {},
                ...(departureDate && { departureDate }),
            },
            ambiliftRequired: passenger.ambiliftRequired ?? false
        };

        return paxObj
    }, [flightInfo, flightState.flightNumber, dbUser]);

    // OPTIMIZED: Memoized add booking handler
    const addBooking = useCallback((additionalBoard, departureDate = null) => {
        let list = [];

        console.log("Preparing to add additional passengers:", additionalPassengers);

        additionalPassengers.forEach(passenger => {
            let doc = createBookingDocument(passenger, additionalBoard, departureDate);
            list.push(doc)
        });

        console.log("Adding additional passengers to flight:", list);

        // post booking list
        dispatch(addFlightPassenger({
            bookings: list,
            iata: iata
        }));

    }, [additionalPassengers, createBookingDocument, dispatch, iata]);

    // OPTIMIZED: Memoized close modal handler
    const closeModal = useCallback(() => {
        dispatch(setShowAddPassenger(null));
        dispatch(resetAdditionalPassengers())
        dispatch(resetAdditionalPassengersFormFlags())
        dispatch(resetAddFlightPassenger())
    }, [dispatch]);

    // OPTIMIZED: Memoized handle right click
    const handleRightClick = useCallback((e) => {
        e.preventDefault();

        // reset
        dispatch(resetMenuContext());

        // set position
        dispatch(setFlightContextPoints({ x: e.pageX, y: e.pageY }));

        // set flight info
        dispatch(setSelectedFlightForContext({
            flightNumber: props?.flight,
            flightToExpand: props?.flightKey,
            flightInfo: flightInfo
        }));

        // show modal
        dispatch(showFlightContext(true));
    }, [props?.flight, props?.flightKey, flightInfo, dispatch]);

    // OPTIMIZED: Memoized handle click to view flight
    const handleViewFlight = useCallback(() => {
        // Don't open for ALTPickUp or estimated flights
        if (props?.flight?.includes("ALTPickUp") || props?.flight?.includes("Estimated")) return;

        if (!flightInfo) return;

        // Set the selected flight - the view-flight.js useEffect will fetch the passenger list
        dispatch(setSelectedFlightToView(flightInfo));
        dispatch(setFlightModal(true));
    }, [props?.flight, flightInfo, dispatch]);
    //#endregion

    // Close modal when passenger is added successfully
    useEffect(() => {
        // addFlightPassengerStatus is global; guard so only the open/active flight modal reacts
        if (showAddPassenger !== props?.flight) return;

        if (addFlightPassengerStatus === 'succeeded') {
            closeModal();
            props?.handleGetBookings?.();
        }
    }, [addFlightPassengerStatus, closeModal, showAddPassenger, props?.flight, props?.handleGetBookings]);

    const isCancelled = flightInfo?.operatingStatus === 'Cancelled' || flightInfo?.operatingStatus === 'cancelled' || flightInfo?.cancelled;

    return (
        <div style={{
            zIndex: 254,
            backgroundColor: isCancelled ? '#ffcccc' : 'transparent',
            borderRadius: isCancelled ? 4 : 0,
        }}>
            <FlightInfoCard
                iata={iata}
                flight={props.flight}
                flightKey={props.flightKey}
                flightData={flightInfo}
                flightState={flightState}
                landedFlight={landedFlight}
                flightStatus={flightStatus}
                openModal={openModal}
                handleViewFlight={handleViewFlight}
                handleRightClick={handleRightClick}
                hasEmptyPRMs={props?.hasEmptyPRMs}
                boarding={props?.boarding}
            />

            {/* create additional passenger on flight modal */}
            {showAddPassenger == props.flight &&
                <ActionsModal
                    show={showAddPassenger == props.flight}
                    handleClose={closeModal}
                    title={"Add PRM Passenger to " + flightState.displayFlightNumber}
                    subtitle={"Please fill in the details of the additional passenger you want to add to flight " + flightState.displayFlightNumber + " and click the 'Add Passenger' button."}
                    isTimeline={true}
                >
                    <AdditionalPassengerForm type={flightState.type} addBooking={addBooking} flight={props.flight} handleClose={closeModal} loading={addFlightPassengerStatus === 'loading'} />
                </ActionsModal>}
        </div>

    );
};

// OPTIMIZED: Proper React.memo with comparison function //
export default React.memo(FlightInformation, (prevProps, nextProps) => {
    return (
        prevProps.flight === nextProps.flight &&
        prevProps.flightKey === nextProps.flightKey &&
        prevProps.flightData === nextProps.flightData &&
        prevProps.boarding === nextProps.boarding
    );
});